
import { Prop, Vue, Watch } from 'vue-property-decorator'
import Component from 'vue-class-component'

import {
  PackageItemsReceivedStatistics,
  PackageItemsStatisticsData,
  PackagesReceivedStatistics,
  PackagesStatisticsData
} from '@/types/graphql'
import { ApexOptions } from 'apexcharts'
import moment from 'moment'
import { DocumentNode } from 'graphql'

// You can declare mixins as the same style as components.

type PackageItemStatistics = {
    hgv_package_items: PackageItemsStatisticsData[]
    libert_package_items: PackageItemsStatisticsData[]
    siemens_package_items: PackageItemsStatisticsData[]
}

type PackageStatistics = {
    hgv_packages: PackagesStatisticsData[]
    libert_packages: PackagesStatisticsData[]
    siemens_packages: PackagesStatisticsData[]
}

@Component
export class DataChartMixin<T extends DocumentNode, G extends DocumentNode> extends Vue {
    // Props
    @Prop({ required: false, default: 'Package' }) protected dataType!: 'Package' | 'PackageItem'

    // Data
    protected isLoading = false;

    protected chartWidth = 0;

    protected chartRenderKey = 0;

    protected en = require('apexcharts/dist/locales/en.json')
    protected fr = require('apexcharts/dist/locales/fr.json')

    protected barChartCategories: string[] = []

    protected packagesStatisticsQuery!: G;

    protected packagesQueryName!: string;

    protected packagesQueryResult: PackageStatistics | null = null;

    protected packageItemsStatisticsQuery!: T;

    protected packageItemsQueryName!: string;

    protected packageItemsQueryResult: PackageItemStatistics | null = null;

    // Computed
    protected get chartColor() {
      const as24HgvColor = 'hsl(50, 90%, 50%)'
      const as24LibertColor = 'hsl(190, 75%, 50%)'
      const as24ObuColor = 'hsl(75, 90%, 45%)'
      const teHgvColor = 'hsl(40, 80%, 50%)'
      const teLibertColor = 'hsl(200, 75%, 45%)'
      const teObuColor = 'hsl(105, 80%, 40%)'
      const circlekHgvColor = 'hsl(30, 70%, 50%)'
      const circlekLibertColor = 'hsl(210, 75%, 40%)'
      const circlekObuColor = 'hsl(135, 70%, 35%)'

      return [
        as24HgvColor,
        teHgvColor,
        circlekHgvColor,
        as24LibertColor,
        teLibertColor,
        circlekLibertColor,
        as24ObuColor,
        teObuColor,
        circlekObuColor
      ]
    }

    protected get barChartSeries(): ApexAxisChartSeries {
      if (this.dataType === 'PackageItem' && this.packageItemsQueryResult) {
        const hgvAs24PackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_as24')
        const hgvTePackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_total_energies')
        const hgvCirclekPackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_circlek')

        const hgvAs24ResultsFound = hgvAs24PackageItemsCountList.length > 0 && !hgvAs24PackageItemsCountList.every(item => item === 0)
        const hgvTeResultsFound = hgvTePackageItemsCountList.length > 0 && !hgvTePackageItemsCountList.every(item => item === 0)
        const hgvCirclekResultsFound = hgvCirclekPackageItemsCountList.length > 0 && !hgvCirclekPackageItemsCountList.every(item => item === 0)

        const libertAs24PackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_as24')
        const libertTePackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_total_energies')
        const libertCirclekPackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_circlek')

        const libertAs24ResultsFound = libertAs24PackageItemsCountList.length > 0 && !libertAs24PackageItemsCountList.every(item => item === 0)
        const libertTeResultsFound = libertTePackageItemsCountList.length > 0 && !libertTePackageItemsCountList.every(item => item === 0)
        const libertCirclekResultsFound = libertCirclekPackageItemsCountList.length > 0 && !libertCirclekPackageItemsCountList.every(item => item === 0)

        const siemensAs24PackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_as24')
        const siemensTePackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_total_energies')
        const siemensCirclekPackageItemsCountList = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_circlek')

        const siemensAs24ResultsFound = siemensAs24PackageItemsCountList.length > 0 && !siemensAs24PackageItemsCountList.every(item => item === 0)
        const siemensTeResultsFound = siemensTePackageItemsCountList.length > 0 && !siemensTePackageItemsCountList.every(item => item === 0)
        const siemensCirclekResultsFound = siemensCirclekPackageItemsCountList.length > 0 && !siemensCirclekPackageItemsCountList.every(item => item === 0)

        const series = []

        if (hgvAs24ResultsFound) {
          series.push({ name: 'AS24 - BADGE PASSANGO', data: hgvAs24PackageItemsCountList })
        }

        if (hgvTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - BADGE PASSANGO', data: hgvTePackageItemsCountList })
        }

        if (hgvCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - BADGE PASSANGO', data: hgvCirclekPackageItemsCountList })
        }

        if (libertAs24ResultsFound) {
          series.push({ name: 'AS24 - BADGE LIBERT', data: libertAs24PackageItemsCountList })
        }

        if (libertTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - BADGE LIBERT', data: libertTePackageItemsCountList })
        }

        if (libertCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - BADGE LIBERT', data: libertCirclekPackageItemsCountList })
        }

        if (siemensAs24ResultsFound) {
          series.push({ name: 'AS24 - OBU PASSANGO', data: siemensAs24PackageItemsCountList })
        }

        if (siemensTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - OBU PASSANGO', data: siemensTePackageItemsCountList })
        }

        if (siemensCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - OBU PASSANGO', data: siemensCirclekPackageItemsCountList })
        }

        return series
      }

      if (this.dataType === 'Package' && this.packagesQueryResult) {
        const hgvAs24PackagesCountList = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_as24')
        const hgvTePackagesCountList = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_total_energies')
        const hgvCirclekPackagesCountList = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_circlek')

        const hgvAs24ResultsFound = hgvAs24PackagesCountList.length > 0 && !hgvAs24PackagesCountList.every(item => item === 0)
        const hgvTeResultsFound = hgvTePackagesCountList.length > 0 && !hgvTePackagesCountList.every(item => item === 0)
        const hgvCirclekResultsFound = hgvCirclekPackagesCountList.length > 0 && !hgvCirclekPackagesCountList.every(item => item === 0)

        const libertTePackagesCountList = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_total_energies')
        const libertAs24PackagesCountList = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_as24')
        const libertCirclekPackagesCountList = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_circlek')

        const libertAs24ResultsFound = libertAs24PackagesCountList.length > 0 && !libertAs24PackagesCountList.every(item => item === 0)
        const libertTeResultsFound = libertTePackagesCountList.length > 0 && !libertTePackagesCountList.every(item => item === 0)
        const libertCirclekResultsFound = libertCirclekPackagesCountList.length > 0 && !libertCirclekPackagesCountList.every(item => item === 0)

        const siemensAs24PackagesCountList = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_as24')
        const siemensTePackagesCountList = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_total_energies')
        const siemensCirclekPackagesCountList = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_circlek')

        const siemensAs24ResultsFound = siemensAs24PackagesCountList.length > 0 && !siemensAs24PackagesCountList.every(item => item === 0)
        const siemensTeResultsFound = siemensTePackagesCountList.length > 0 && !siemensTePackagesCountList.every(item => item === 0)
        const siemensCirclekResultsFound = siemensCirclekPackagesCountList.length > 0 && !siemensCirclekPackagesCountList.every(item => item === 0)

        const series = []

        if (hgvAs24ResultsFound) {
          series.push({ name: 'AS24 - BADGE PASSANGO', data: hgvAs24PackagesCountList })
        }

        if (hgvTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - BADGE PASSANGO', data: hgvTePackagesCountList })
        }

        if (hgvCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - BADGE PASSANGO', data: hgvCirclekPackagesCountList })
        }

        if (libertAs24ResultsFound) {
          series.push({ name: 'AS24 - BADGE LIBERT', data: libertAs24PackagesCountList })
        }

        if (libertTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - BADGE LIBERT', data: libertTePackagesCountList })
        }

        if (libertCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - BADGE LIBERT', data: libertCirclekPackagesCountList })
        }

        if (siemensAs24ResultsFound) {
          series.push({ name: 'AS24 - OBU PASSANGO', data: siemensAs24PackagesCountList })
        }

        if (siemensTeResultsFound) {
          series.push({ name: 'TOTAL ENERGIES - OBU PASSANGO', data: siemensTePackagesCountList })
        }

        if (siemensCirclekResultsFound) {
          series.push({ name: 'CIRCLEK - OBU PASSANGO', data: siemensCirclekPackagesCountList })
        }

        return series
      }

      return []
    }

    protected get barChartOptions(): ApexOptions {
      return {
        chart: {
          type: 'line',
          locales: [this.en, this.fr],
          defaultLocale: this.$i18n.locale,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        markers: {
          size: 2
        },
        colors: this.chartColor,
        dataLabels: {
          enabled: false
        },
        plotOptions: {
          bar: {
            columnWidth: '20%'
          }
        },
        xaxis: {
          categories: this.barChartCategories,
          axisBorder: {
            show: true
          },
          tooltip: {
            enabled: false
          },
          labels: {
            show: true,
            formatter: (value: string) => moment(value).format('DD.MM'),
            rotate: -45
          },
          type: 'datetime'
        },
        yaxis: {
          axisBorder: {
            show: true
          },
          labels: {
            show: true
          },
          tooltip: {
            enabled: false
          }
        },
        legend: {
          horizontalAlign: 'left',
          offsetX: 40
        }
      }
    }

    protected get noDataForStatistics(): boolean {
      if (this.dataType === 'PackageItem' && this.packageItemsQueryResult) {
        const hgvAs24PackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_as24')
        const libertAs24PackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_as24')
        const siemensAs24PackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_as24')

        const hgvTePackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_total_energies')
        const libertTePackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_total_energies')
        const siemensTePackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_total_energies')

        const hgvCirclekPackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.hgv_package_items, 'package_items_count_for_circlek')
        const libertCirclekPackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.libert_package_items, 'package_items_count_for_circlek')
        const siemensCirclekPackageItemsCount = this.getPackageItemCountList(this.packageItemsQueryResult.siemens_package_items, 'package_items_count_for_circlek')

        return (
          hgvAs24PackageItemsCount.every(item => item === 0) &&
            libertAs24PackageItemsCount.every(item => item === 0) &&
            siemensAs24PackageItemsCount.every(item => item === 0) &&
            hgvTePackageItemsCount.every(item => item === 0) &&
            libertTePackageItemsCount.every(item => item === 0) &&
            siemensTePackageItemsCount.every(item => item === 0) &&
            hgvCirclekPackageItemsCount.every(item => item === 0) &&
            libertCirclekPackageItemsCount.every(item => item === 0) &&
            siemensCirclekPackageItemsCount.every(item => item === 0)
        )
      }

      if (this.dataType === 'Package' && this.packagesQueryResult) {
        const hgvAs24PackagesCount = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_as24')
        const libertAs24PackagesCount = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_as24')
        const siemensAs24PackagesCount = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_as24')

        const hgvTePackagesCount = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_total_energies')
        const libertTePackagesCount = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_total_energies')
        const siemensTePackagesCount = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_total_energies')

        const hgvCirclekPackagesCount = this.getPackagesCountList(this.packagesQueryResult.hgv_packages, 'packages_count_for_circlek')
        const libertCirclekPackagesCount = this.getPackagesCountList(this.packagesQueryResult.libert_packages, 'packages_count_for_circlek')
        const siemensCirclekPackagesCount = this.getPackagesCountList(this.packagesQueryResult.siemens_packages, 'packages_count_for_circlek')

        return (
          hgvAs24PackagesCount.every(item => item === 0) &&
            libertAs24PackagesCount.every(item => item === 0) &&
            siemensAs24PackagesCount.every(item => item === 0) &&
            hgvTePackagesCount.every(item => item === 0) &&
            libertTePackagesCount.every(item => item === 0) &&
            siemensTePackagesCount.every(item => item === 0) &&
            hgvCirclekPackagesCount.every(item => item === 0) &&
            libertCirclekPackagesCount.every(item => item === 0) &&
            siemensCirclekPackagesCount.every(item => item === 0)
        )
      }

      return true
    }

    // ⁡⁢⁣⁣➍ ⁡⁢⁣⁣𝗠𝗲𝘁𝗵𝗼𝗱𝘀⁡

    protected async getPackageItemsStatistics() {
      this.isLoading = true

      try {
        const result = await this.$apollo
          .query({
            query: this.packageItemsStatisticsQuery,
            fetchPolicy: 'network-only'
          })

        const statistics = result.data[this.packageItemsQueryName]

        this.fillBarChartCategoriesWithPackageItemsData(statistics)
        this.packageItemsQueryResult = statistics
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
        this.$emit('done')
      }
    }

    protected async getPackagesStatistics() {
      this.isLoading = true

      try {
        const result = await this.$apollo
          .query({
            query: this.packagesStatisticsQuery,
            fetchPolicy: 'network-only'
          })

        const statistics = result.data[this.packagesQueryName]

        this.fillBarChartCategoriesWithPackagesData(statistics)
        this.packagesQueryResult = statistics
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
        this.$emit('done')
      }
    }

    protected setOptimalChartSize(val: any) {
      this.chartWidth = Math.round(val.elementWidth) - 20
    }

    protected fillBarChartCategoriesWithPackagesData(data: PackagesReceivedStatistics) {
      const { hgv_packages, libert_packages, siemens_packages } = data

      if (hgv_packages) {
        this.barChartCategories = hgv_packages.slice().reverse().map(
          item => item.date
        )
      }

      if (libert_packages) {
        this.barChartCategories = libert_packages.slice().reverse().map(
          item => item.date
        )
      }

      if (siemens_packages) {
        this.barChartCategories = siemens_packages.slice().reverse().map(
          item => item.date
        )
      }
    }

    protected fillBarChartCategoriesWithPackageItemsData(data: PackageItemsReceivedStatistics) {
      const { hgv_package_items, libert_package_items, siemens_package_items } = data

      if (hgv_package_items) {
        this.barChartCategories = hgv_package_items.slice().reverse().map(
          item => item.date
        )
      }

      if (libert_package_items) {
        this.barChartCategories = libert_package_items.slice().reverse().map(
          item => item.date
        )
      }

      if (siemens_package_items) {
        this.barChartCategories = siemens_package_items.slice().reverse().map(
          item => item.date
        )
      }
    }

    protected getPackageItemCountList(
      packageData: PackageItemsStatisticsData[],
      packageItemType: keyof PackageItemsStatisticsData
    ) {
      if (Array.isArray(packageData)) {
        return this.barChartCategories.map(currentDate => Number(packageData.find(item => item.date === currentDate)?.[packageItemType]) || 0)
      }

      return []
    }

    protected getPackagesCountList(
      packageData: PackagesStatisticsData[],
      packageItemType: keyof PackagesStatisticsData
    ) {
      if (Array.isArray(packageData)) {
        return this.barChartCategories.map(currentDate => Number(packageData.find(item => item.date === currentDate)?.[packageItemType]) || 0)
      }

      return []
    }

    // ⁡⁢⁣⁣➎ ⁡⁢⁣⁣𝗪𝗮𝘁𝗰𝗵𝗲𝗿𝘀⁡

    @Watch('$i18n.locale')
    protected onI18nLocaleChange() {
      this.chartRenderKey += 1
    }

    @Watch('dataType')
    protected onDataTypeFilterChange(dataType: 'PackageItem' | 'Package') {
      switch (dataType) {
        case 'PackageItem':
          this.getPackageItemsStatistics()
          break
        case 'Package':
          this.getPackagesStatistics()
          break
      }
    }

    // ⁡⁢⁣⁣➏ 𝗛𝗼𝗼𝗸𝘀⁡

    protected mounted() {
      switch (this.dataType) {
        case 'PackageItem':
          this.getPackageItemsStatistics()
          break
        case 'Package':
          this.getPackagesStatistics()
          break
      }
    }
}
